<template>
  <div class="cadastrar-form">
    <Breadcrumb :items="breadcrumbItems" />
    <div class="titulo-form">
      <h3>{{ isEdicao ? "Edição de Encontros" : "Cadastro de Encontros" }}</h3>
    </div>

    <div class="form-layout">
      <div class="col-left">
        <FormEncontroInputs
          v-model="encontroAtual"
          :data-minima="dataMinima"
          @adicionar="adicionarEncontro"
        />
      </div>

      <div class="col-right">
        <ListaEncontrosAdicionados
          :encontros="encontrosAdicionados"
          @editar="editarEncontro"
          @remover="removerEncontro"
        />
      </div>
    </div>

    <div class="cadastro-form-container">
      <button
        @click="voltar"
        class="voltar-btn"
        type="button"
      >
        Voltar
      </button>

      <button
        v-if="isEdicao"
        @click="restaurarEncontrosOriginais"
        class="restaurar-btn"
        type="button"
        :disabled="isSaving || isSavingEncontros || isLoadingEncontros"
      >
        {{ isLoadingEncontros ? "Carregando..." : "Restaurar dados originais" }}
      </button>

      <div class="cadastro-form">
        <button
          type="button"
          @click="cadastrarEncontros"
          :disabled="
            encontrosAdicionados.length === 0 || isSaving || isSavingEncontros
          "
        >
          {{
            isSaving || isSavingEncontros
              ? "Salvando..."
              : isEdicao
                ? "Confirmar edição"
                : atividade.criarNoticia
                  ? "Cadastrar Notícia"
                  : "Prosseguir cadastro"
          }}
        </button>
      </div>
    </div>
  </div>

  <ModalDeConfirmacao
    v-if="modalConfirmacao.mostrar"
    :texto="modalConfirmacao.texto"
    @confirmacao="tratarConfirmacaoCadastro"
  />

  <Aviso
    v-if="aviso.mostrar"
    :sucesso="aviso.sucesso"
    :texto="aviso.mensagem"
    @fecharModal="aviso.mostrar = false"
  />
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { useRouter, useRoute, onBeforeRouteLeave } from "vue-router";

import Breadcrumb from "@/components/common/Breadcrumb.vue";
import ModalDeConfirmacao from "@/components/common/modalDeConfirmacao.vue";
import Aviso from "@/components/common/aviso.vue";
import FormEncontroInputs from "./FormEncontroInputs.vue";
import ListaEncontrosAdicionados from "./ListaEncontrosAdicionados.vue";

import { useAtividades } from "@/composables/useAtividades";
import { useEncontros } from "@/composables/useEncontros";
import { useValidacaoEncontros } from "@/composables/useValidacaoEncontros";
import acaoService from "@/service/acaoService";

const props = defineProps({
  acaoId: {
    type: String,
    default: "",
  },
  atividadeId: {
    type: String,
    default: "",
  },
  dadosAtividade: {
    type: Object,
    default: null,
  },
  vagasLimitadas: {
    type: Boolean,
    default: false,
  },
});

const router = useRouter();
const route = useRoute();
const { saveAtividade, fetchAtividadeById, isSaving } = useAtividades();
const {
  fetchEncontros,
  createEncontro,
  createMuitosEncontros,
  updateEncontro,
  deleteEncontro,
  isLoading: isLoadingEncontros,
  isSaving: isSavingEncontros,
} = useEncontros();
const {
  criarEncontroPadrao,
  temEdicaoPendente,
  extrairDuracaoEncontro,
  calcularFimPeriodo,
  formatarEncontro,
  formatarEncontroParaEnvio,
  validarDuracao,
  validarDatasEncontros,
  calcularDataMinima,
} = useValidacaoEncontros();

function carregarDadosIniciaisAtividade() {
  if (props.dadosAtividade && props.dadosAtividade.nome) {
    return { ...props.dadosAtividade };
  }
  const dadosSalvos = sessionStorage.getItem("cadastro-atividade-temp");
  if (dadosSalvos) {
    try {
      return JSON.parse(dadosSalvos);
    } catch (e) {
      console.error("Erro ao ler dados da atividade do sessionStorage:", e);
    }
  }
  return {};
}

const atividade = ref(carregarDadosIniciaisAtividade());
const encontroAtual = ref(criarEncontroPadrao());
const encontrosAdicionados = ref(
  Array.isArray(atividade.value.encontros)
    ? atividade.value.encontros.map(formatarEncontro)
    : []
);
const encontrosOriginais = ref(
  Array.isArray(atividade.value.encontrosOriginais)
    ? atividade.value.encontrosOriginais.map(formatarEncontro)
    : []
);

const targetAtividadeId = computed(
  () =>
    props.atividadeId ||
    route.params.atividadeId ||
    atividade.value?.id ||
    atividade.value?.atividadeId ||
    ""
);
const isEdicao = computed(() => Boolean(targetAtividadeId.value));

const modalConfirmacao = ref({
  mostrar: false,
  texto: "",
});

const aviso = ref({
  mostrar: false,
  mensagem: "",
  sucesso: false,
});

const dataMinima = computed(() =>
  calcularDataMinima(
    atividade.value?.dataFimInscricao,
    encontrosAdicionados.value
  )
);

const breadcrumbItems = computed(() => {
  const targetAcaoId =
    props.acaoId || route?.params?.id || atividade.value?.acaoExtensaoId;

  if (isEdicao.value) {
    return [
      { label: "Início", to: "/" },
      { label: "Ações", to: "/suas-acoes" },
      { tipo: "acao", id: targetAcaoId, to: `/acao/${targetAcaoId}` },
      { label: "Atividades", to: `/acao/${targetAcaoId}/atividades` },
      {
        label: atividade.value?.nome || "Atividade",
        to: `/acao/${targetAcaoId}/atividades/${targetAtividadeId.value}`,
      },
      {
        label: "Editar Atividade",
        to: `/acao/${targetAcaoId}/editar-atividade/${targetAtividadeId.value}`,
      },
      { label: "Editar Encontros", to: "" },
    ];
  }

  return [
    { label: "Início", to: "/" },
    { label: "Ações", to: "/suas-acoes" },
    { tipo: "acao", id: targetAcaoId, to: `/acao/${targetAcaoId}` },
    { label: "Atividades", to: `/acao/${targetAcaoId}/atividades` },
    {
      label: "Cadastrar Atividade",
      to: `/acao/${targetAcaoId}/cadastrar-atividade`,
    },
    { label: "Cadastrar Encontro", to: "" },
  ];
});

const resetarEncontroAtual = () => {
  encontroAtual.value = criarEncontroPadrao();
};

function salvarEstadoTemporario() {
  const dadosSalvos = sessionStorage.getItem("cadastro-atividade-temp");
  let dadosAtividade = {};
  if (dadosSalvos) {
    try {
      dadosAtividade = JSON.parse(dadosSalvos);
    } catch (e) {
      console.error("Erro ao ler dados da atividade do sessionStorage:", e);
    }
  }

  const payloadAtualizado = {
    ...dadosAtividade,
    ...atividade.value,
    encontros: encontrosAdicionados.value,
    encontrosOriginais: encontrosOriginais.value,
  };

  sessionStorage.setItem(
    "cadastro-atividade-temp",
    JSON.stringify(payloadAtualizado)
  );
}

function validarDatasDosEncontros() {
  const resultado = validarDatasEncontros(
    encontrosAdicionados.value,
    atividade.value?.dataFimInscricao
  );
  if (!resultado.valido) {
    aviso.value = {
      mostrar: true,
      sucesso: false,
      mensagem: resultado.mensagem,
    };
    return false;
  }
  return true;
}

function adicionarEncontro() {
  const validacaoDuracao = validarDuracao(
    encontroAtual.value.duracaoHoras,
    encontroAtual.value.duracaoMinutos
  );

  if (!validacaoDuracao.valido) {
    aviso.value = {
      mostrar: true,
      sucesso: false,
      mensagem: validacaoDuracao.mensagem,
    };
    return;
  }

  const { duracaoHoras, duracaoMinutos, duracao } = extrairDuracaoEncontro(
    encontroAtual.value
  );
  const fimPeriodo = calcularFimPeriodo(
    encontroAtual.value.inicio_periodo,
    duracaoHoras,
    duracaoMinutos
  );

  encontrosAdicionados.value.push({
    ...encontroAtual.value,
    duracaoHoras,
    duracaoMinutos,
    duracao,
    fim_periodo: fimPeriodo,
  });

  salvarEstadoTemporario();
  resetarEncontroAtual();
}

function editarEncontro(encontro, index) {
  if (!temEdicaoPendente(encontroAtual.value)) {
    encontroAtual.value = formatarEncontro(encontro);
    removerEncontro(index);
  } else {
    aviso.value = {
      mostrar: true,
      sucesso: false,
      mensagem: "Conclua a edição do encontro atual antes de editar outro.",
    };
  }
}

function removerEncontro(index) {
  encontrosAdicionados.value.splice(index, 1);
  salvarEstadoTemporario();
}

function voltar() {
  salvarEstadoTemporario();

  if (window.history.length > 1) {
    router.back();
  } else {
    const targetAcaoId = props.acaoId || route.params.id;
    if (targetAcaoId) {
      if (isEdicao.value) {
        router.push({
          name: "editar-atividade",
          params: { id: targetAcaoId, atividadeId: targetAtividadeId.value },
        });
      } else {
        router.push({
          name: "cadastrar-atividade",
          params: { id: targetAcaoId },
        });
      }
    }
  }
}

async function carregarEncontrosDaAtividade(atividadeId) {
  if (!atividadeId) return false;
  try {
    const listaEncontros = await fetchEncontros(0, 1000, atividadeId);
    if (Array.isArray(listaEncontros)) {
      const formatados = listaEncontros.map(formatarEncontro);
      encontrosOriginais.value = JSON.parse(JSON.stringify(formatados));
      encontrosAdicionados.value = formatados;
      salvarEstadoTemporario();
      return true;
    }
  } catch (err) {
    console.error("Erro ao buscar encontros da atividade:", err);
  }
  return false;
}

async function restaurarEncontrosOriginais() {
  if (!targetAtividadeId.value) return;
  const sucesso = await carregarEncontrosDaAtividade(targetAtividadeId.value);
  if (sucesso) {
    resetarEncontroAtual();
    aviso.value = {
      mostrar: true,
      sucesso: true,
      mensagem: "Encontros originais restaurados com sucesso!",
    };
  } else {
    aviso.value = {
      mostrar: true,
      sucesso: false,
      mensagem: "Erro ao buscar os encontros originais da atividade.",
    };
  }
}

function cadastrarEncontros() {
  if (temEdicaoPendente(encontroAtual.value)) {
    aviso.value = {
      mostrar: true,
      sucesso: false,
      mensagem: "Adicione ou conclua o encontro atual antes de prosseguir.",
    };
    return;
  }

  if (encontrosAdicionados.value.length === 0) {
    aviso.value = {
      mostrar: true,
      sucesso: false,
      mensagem:
        "Adicione pelo menos um encontro para prosseguir com o cadastro.",
    };
    return;
  }

  if (!validarDatasDosEncontros()) {
    return;
  }

  modalConfirmacao.value = {
    mostrar: true,
    texto: isEdicao.value
      ? "Ao confirmar os encontros você irá atualizar os encontros da atividade, tem certeza que deseja prosseguir com a ação?"
      : atividade.value?.criarNoticia
        ? "Ao salvar os encontros você irá prosseguir para o cadastro da notícia, tem certeza que deseja continuar?"
        : "Ao cadastrar os encontros você irá avançar no cadastro da atividade, tem certeza que deseja prosseguir com a ação?",
  };
}

async function salvarEncontrosEdicao() {
  const targetId = targetAtividadeId.value;

  // 1. Deleta encontros originais que foram removidos
  const encontrosParaDeletar = encontrosOriginais.value.filter((orig) => {
    return (
      orig.encontroId &&
      !encontrosAdicionados.value.some(
        (atual) => atual.encontroId === orig.encontroId
      )
    );
  });
  for (const enc of encontrosParaDeletar) {
    await deleteEncontro(enc.encontroId);
  }

  // 2. Atualiza encontros existentes modificados/mantidos
  const encontrosParaAtualizar = encontrosAdicionados.value.filter(
    (e) => e.encontroId
  );
  for (const enc of encontrosParaAtualizar) {
    await updateEncontro(enc.encontroId, formatarEncontroParaEnvio(enc));
  }

  // 3. Cadastra novos encontros adicionados
  const novosEncontros = encontrosAdicionados.value.filter(
    (e) => !e.encontroId
  );
  if (novosEncontros.length === 1) {
    await createEncontro(
      targetId,
      formatarEncontroParaEnvio(novosEncontros[0])
    );
  } else if (novosEncontros.length > 1) {
    const payloadLista = novosEncontros.map(formatarEncontroParaEnvio);
    await createMuitosEncontros(targetId, payloadLista);
  }
}

async function tratarConfirmacaoCadastro(confirmado) {
  modalConfirmacao.value.mostrar = false;
  if (!confirmado) return;

  if (!validarDatasDosEncontros()) {
    return;
  }

  const targetAcaoId = props.acaoId || route.params.id;

  try {
    if (isEdicao.value) {
      await salvarEncontrosEdicao();
      sessionStorage.removeItem("cadastro-atividade-temp");
      router.push(
        `/acao/${targetAcaoId}/atividades/${targetAtividadeId.value}`
      );
    } else {
      if (atividade.value.imagem?.base64Data === "") {
        atividade.value.imagem = null;
      }
      const payload = {
        ...atividade.value,
        encontros: encontrosAdicionados.value.map(formatarEncontroParaEnvio),
      };

      // Fallback de imagem da ação pai caso a atividade não tenha imagem própria
      if (!payload.imagem || !payload.imagem.base64Data) {
        try {
          if (targetAcaoId) {
            const dadosAcao = await acaoService.getAcoesById(targetAcaoId);
            if (dadosAcao?.imagem?.base64Data) {
              payload.imagem = {
                mimeType: dadosAcao.imagem.mimeType || "image/png",
                base64Data: dadosAcao.imagem.base64Data,
                descricaoImagem:
                  dadosAcao.descricaoImagem ||
                  `Imagem da ação ${dadosAcao.titulo || ""}`,
                creditosImagem: dadosAcao.imagem.creditosImagem || "",
              };
            } else if (dadosAcao?.imagemUrl) {
              payload.imagemUrl = dadosAcao.imagemUrl;
              payload.descricaoImagem =
                dadosAcao.descricaoImagem ||
                `Imagem da ação ${dadosAcao.titulo || ""}`;
              payload.imagem = null;
            } else {
              payload.imagem = null;
            }
          } else {
            payload.imagem = null;
          }
        } catch (e) {
          console.error("Erro ao buscar imagem padrão da ação:", e);
          payload.imagem = null;
        }
      }

      if (payload.criarNoticia) {
        sessionStorage.setItem("atividade-em-cadastro", JSON.stringify(payload));
        sessionStorage.removeItem("cadastro-atividade-temp");
        router.push({
          name: "cadastrar-noticia",
          params: { id: targetAcaoId },
          state: { atividadeObjeto: payload },
        });
        return;
      }

      const atividadeSalva = await saveAtividade(payload);
      sessionStorage.removeItem("cadastro-atividade-temp");
      sessionStorage.removeItem("atividade-em-cadastro");

      const finalAtividadeId = atividadeSalva?.id || targetAtividadeId.value;
      router.push(`/acao/${targetAcaoId}/atividades/${finalAtividadeId}`);
    }
  } catch (error) {
    console.error("Ocorreu um erro ao salvar encontros:", error);
    aviso.value = {
      mostrar: true,
      sucesso: false,
      mensagem: isEdicao.value
        ? "Ocorreu um erro ao atualizar os encontros da atividade."
        : "Ocorreu um erro ao cadastrar a atividade com os encontros.",
    };
  }
}

watch(
  () => props.dadosAtividade,
  (novosDados) => {
    if (novosDados && Object.keys(novosDados).length > 0) {
      atividade.value = { ...atividade.value, ...novosDados };
      if (novosDados.encontros?.length) {
        encontrosAdicionados.value = novosDados.encontros.map(formatarEncontro);
      }
    }
  },
  { deep: true }
);

const rotasDoFluxo = [
  "cadastrar-atividade",
  "cadastrar-encontros-atividade",
  "editar-atividade",
  "editar-encontros-atividade",
  "cadastrar-noticia",
];

onBeforeRouteLeave((to) => {
  if (!rotasDoFluxo.includes(to.name)) {
    sessionStorage.removeItem("cadastro-atividade-temp");
  }
});

async function carregarDadosAtividade(atividadeId) {
  if (!atividadeId) return;
  try {
    const dados = await fetchAtividadeById(atividadeId);
    if (dados) {
      atividade.value = { ...dados };
    }
  } catch (e) {
    console.error("Erro ao buscar dados da atividade:", e);
  }
}

onMounted(async () => {
  if (isEdicao.value && targetAtividadeId.value) {
    if (!atividade.value || !atividade.value.nome) {
      await carregarDadosAtividade(targetAtividadeId.value);
    }

    if (encontrosAdicionados.value.length === 0) {
      await carregarEncontrosDaAtividade(targetAtividadeId.value);
    }
    return;
  }

  if (!atividade.value || !atividade.value.nome) {
    const targetAcaoId = props.acaoId || route.params.id;
    if (targetAcaoId) {
      router.push({
        name: "cadastrar-atividade",
        params: { id: targetAcaoId },
      });
    }
  }
});
</script>

<style scoped>
.cadastro-form-container {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 15px;
  margin-top: 1.5rem;
}

.voltar-btn {
  background-color: white;
  color: var(--roxo-escuro, #6f319a);
  border: 1px solid var(--roxo-escuro, #6f319a);
  padding: 8px 24px;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.voltar-btn:hover {
  background-color: #f3e8f8;
}

.restaurar-btn {
  background-color: white;
  color: #b45309;
  border: 1px solid #d97706;
  padding: 8px 20px;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.9rem;
  white-space: nowrap;
}

.restaurar-btn:hover {
  background-color: #fef3c7;
}

.restaurar-btn:disabled,
.voltar-btn:disabled,
.cadastro-form > button:disabled {
  background-color: #b0bec5 !important;
  color: #ffffff !important;
  border-color: transparent !important;
  cursor: not-allowed !important;
  opacity: 0.7;
}
</style>
